import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import { ThemeProvider } from "./context/ThemeContext";
import LoadingSpinner from "./components/common/LoadingSpinner";
import { Loader } from "lucide-react"; // Imported for OAuthHandler fallback
import { SocketProvider } from "./context/SocketContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Lazy load pages for performance (Code Splitting)
const DashboardOverview = lazy(() => import("./pages/overview/DashboardOverview"));
const TiktokDashboard = lazy(() => import("./pages/TiktokDashboard"));
const ShopeeDashboard = lazy(() => import("./pages/ShopeeDashboard"));
const ProductDashboard = lazy(() => import("./pages/ProductDashboard"));
const InventoryDashboard = lazy(() => import("./pages/InventoryDashboard"));
const LocationDashboard = lazy(() => import("./pages/LocationDashboard"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"));
const Login = lazy(() => import("./pages/Login")); // Adding Login too as it seemed missing or implicit
const DealCalculator = lazy(() => import("./pages/DealCalculator"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OAuthHandler = lazy(() => import("./components/OAuthHandler")); // New OAuth Handler

const UserProfile = lazy(() => import("./pages/UserProfile")); // User Profile Page
const CompensationOrder = lazy(() => import("./pages/CompensationOrder"));

// Global Loading Fallback
const PageLoader = () => (
  <div className="flex h-[calc(100vh-100px)] w-full items-center justify-center">
    <LoadingSpinner />
  </div>
);



// ... (PageLoader defined above)

export default function App() {
  return (
    <ThemeProvider>
      <SocketProvider>
      <Routes>
        <Route path="maintenance" element={
          <Suspense fallback={<PageLoader />}><MaintenancePage /></Suspense>
        } />
        <Route path="/login" element={
          <Suspense fallback={<PageLoader />}><Login /></Suspense>
        } />
        
        {/* TikTok OAuth Routes */}
        <Route path="/oauth-success" element={<Suspense fallback={<Loader />}><OAuthHandler /></Suspense>} />
        <Route path="/oauth-error" element={<Suspense fallback={<Loader />}><OAuthHandler /></Suspense>} />

        <Route element={<MainLayout />}>
        <Route
          path="*"
          element={
            <Suspense fallback={<PageLoader />}>
                {/* Global Listener for ?code=... at root or any page */}
               <OAuthHandler /> 
               <Routes>
                  {/* Default /dashboard/online -> DashboardOverview */}
                  <Route index element={<DashboardOverview />} />
                  
                  <Route path="tiktok" element={<TiktokDashboard />} />

                  <Route path="shopee" element={<ShopeeDashboard />} />
                  <Route path="products" element={<ProductDashboard />} />
                  <Route path="inventory" element={<InventoryDashboard />} />
                  <Route path="location" element={<LocationDashboard />} />
                  <Route path="customers" element={<CustomerDashboard />} />
                  <Route path="admin" element={<AdminDashboard />} />
                  <Route path="admin/users" element={<AdminDashboard />} />
                  <Route path="tools/deal-calculator" element={<DealCalculator />} />
                  <Route path="tools/compensation-order" element={<CompensationOrder />} />
                  <Route path="profile" element={<UserProfile />} />
                  
                  {/* Fallback - 404 Page */}
                  <Route path="*" element={<NotFound />} />
               </Routes>
            </Suspense>
          }
        />
      </Route>
      </Routes>
      <ToastContainer position="top-right" autoClose={5000} />
      </SocketProvider>
    </ThemeProvider>
  );
}
